import { useCallback, useEffect, useState } from 'react'
import { Download, RefreshCw } from 'lucide-react'
import { api } from '@/util/api'
import {
  Button,
  EmptyState,
  IconButton,
  Notice,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  useToast,
} from '@/components/play'
import { WorkspaceInfo } from '@/components/workspace/WorkspaceInfo'
import type { EventResource } from '@/types/events'

/** A registration as `GET /events/:event_id/registrations` lists it. */
export interface EventRegistrationRow {
  id: string
  registered_at: string
  email: string
  name: string | null
  username: string | null
  time_zone: string | null
  notified_at: string | null
}

const PAGE = 200
const WHEN = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })

/**
 * An event's pre-show (docs/features/pre-show-page.md): the switch that puts the countdown and Remind me on its page
 * before it starts, and who registered for the go-live email, newest first, with a CSV export. The same list is
 * `GET /events/:event_id/registrations` and the CLI's `registrations list|export`.
 */
export function EventPreShowSettings({ eventId, event, onSaved }: { eventId: string; event: EventResource; onSaved: (next: EventResource) => void }) {
  const { toast } = useToast()
  const enabled = event.attributes.pre_show === true
  const [saving, setSaving] = useState(false)
  const [rows, setRows] = useState<EventRegistrationRow[] | null>(null)
  const [total, setTotal] = useState(0)
  const [failed, setFailed] = useState(false)
  const [downloading, setDownloading] = useState(false)

  const load = useCallback(() => {
    setFailed(false)
    api.get<{ data: EventRegistrationRow[]; meta: { total: number } }>(`/events/${eventId}/registrations`, { limit: PAGE }).then(
      (res) => {
        setRows(res.data)
        setTotal(res.meta.total)
      },
      () => setFailed(true),
    )
  }, [eventId])
  useEffect(load, [load])

  async function toggle(next: boolean) {
    setSaving(true)
    try {
      const res = await api.patch<{ data: EventResource }>(`/events/${eventId}`, { event: { pre_show: next } })
      onSaved(res.data)
      toast({ title: next ? 'Pre-show on' : 'Pre-show off', tone: 'success' })
    } catch {
      toast({ title: 'Pre-show not changed', description: 'Try again.', tone: 'error' })
    } finally {
      setSaving(false)
    }
  }

  async function download() {
    setDownloading(true)
    try {
      const blob = await api.getBlob(`/events/${eventId}/registrations.csv`)
      const link = Object.assign(document.createElement('a'), {
        href: URL.createObjectURL(blob),
        download: `${event.attributes.shortcode ?? 'event'}-registrations.csv`,
      })
      link.click()
      URL.revokeObjectURL(link.href)
    } catch {
      toast({ title: 'Download failed', description: 'Try again.', tone: 'error' })
    } finally {
      setDownloading(false)
    }
  }

  const missing = [
    !event.attributes.starts_at && 'a start time',
    !event.attributes.timezone && 'a time zone',
  ].filter(Boolean) as string[]

  return (
    <div className="ps-fields">
      <Switch
        label="Pre-show"
        labelAccessory={
          <WorkspaceInfo label="About the pre-show">
            Before it starts, your event page shows a countdown on a globe and Remind me. Everyone who registers is
            emailed once when you go live on your event’s stage. The headline is your subtitle.
          </WorkspaceInfo>
        }
        checked={enabled}
        disabled={saving}
        onCheckedChange={(checked) => void toggle(checked)}
      />
      {enabled && missing.length > 0 && (
        <Notice tone="warning">Add {missing.join(' and ')} under General so the countdown and globe can show.</Notice>
      )}

      <div className="flex items-center justify-between gap-3">
        <h3 className="text-base font-bold" id="registrations-heading">
          Registrations <span className="tabular-nums">{rows ? total.toLocaleString() : ''}</span>
        </h3>
        <div className="flex gap-2">
          <IconButton label="Refresh" variant="ghost" onClick={load}>
            <RefreshCw />
          </IconButton>
          <Button variant="secondary" onClick={() => void download()} loading={downloading} disabled={!rows || total === 0}>
            <Download aria-hidden="true" />
            CSV
          </Button>
        </div>
      </div>

      {failed ? (
        <Notice tone="error" action={<Button onClick={load}>Try again</Button>}>
          Registrations couldn’t load.
        </Notice>
      ) : rows && rows.length === 0 ? (
        <EmptyState title="No registrations yet" description={enabled ? 'They show here as people tap Remind me.' : 'Turn on the pre-show to take them.'} />
      ) : rows ? (
        <Table caption="Registrations" hideCaption aria-describedby="registrations-heading">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Email</TableHeaderCell>
              <TableHeaderCell>Name</TableHeaderCell>
              <TableHeaderCell>Time zone</TableHeaderCell>
              <TableHeaderCell>Registered</TableHeaderCell>
              <TableHeaderCell>Emailed</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.email}</TableCell>
                <TableCell>{row.name ?? (row.username ? `@${row.username}` : '—')}</TableCell>
                <TableCell>{row.time_zone?.replace(/_/g, ' ') ?? '—'}</TableCell>
                <TableCell className="whitespace-nowrap">{WHEN.format(new Date(row.registered_at))}</TableCell>
                <TableCell className="whitespace-nowrap">{row.notified_at ? WHEN.format(new Date(row.notified_at)) : '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : null}
      {rows && total > rows.length && <p className="text-sm">Showing the newest {rows.length}. The CSV has all {total.toLocaleString()}.</p>}
    </div>
  )
}
